import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { authFetch } from '../../utils/authUtils';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Heart toggle that lives on every poster card.
 *
 * `savedSet` is shared across all cards (Discover page owns it) so toggles
 * stay in sync without each card needing its own fetch. We optimistically
 * update local state on click, then call the API; if the API fails we
 * revert + toast.
 */
const SaveHeart = ({ item, savedSet, onChange }) => {
  const refId = item.ref_id || item.book_id || item.series_id;
  const initial = !!(savedSet && savedSet.has(refId));
  const [saved, setSaved] = useState(initial);
  const [busy, setBusy] = useState(false);

  // If the parent set changes (e.g. after a refresh), reflect it.
  React.useEffect(() => {
    setSaved(!!(savedSet && savedSet.has(refId)));
  }, [savedSet, refId]);

  const onToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy || !refId) return;
    setBusy(true);
    const next = !saved;
    setSaved(next);
    try {
      if (next) {
        const r = await authFetch(`${BACKEND_URL}/api/library/saved`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ref_id: refId,
            title: item.title,
            cover_image: item.cover_image,
            type: item.type,
            author: item.author,
            is_series: item.is_series,
          }),
        });
        if (!r.ok) throw new Error('save failed');
        toast.success('Added to My List');
      } else {
        const r = await authFetch(`${BACKEND_URL}/api/library/saved/${encodeURIComponent(refId)}`, {
          method: 'DELETE',
        });
        if (!r.ok) throw new Error('unsave failed');
        toast.success('Removed from My List');
      }
      onChange?.(refId, next);
    } catch (err) {
      // revert
      setSaved(!next);
      toast.error('Could not update My List');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onToggle}
      disabled={busy}
      aria-label={saved ? 'Remove from My List' : 'Add to My List'}
      data-testid={`save-heart-${refId}`}
      className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-md transition-all ${
        saved
          ? 'bg-rose-500/90 text-white shadow-lg shadow-rose-500/30'
          : 'bg-slate-900/60 text-slate-300 hover:bg-slate-900/80 hover:text-white'
      } ${busy ? 'opacity-60' : ''}`}
    >
      <Heart className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} />
    </button>
  );
};

export default SaveHeart;
